import { useClientConfig } from "@/features/client-config/contexts/client-config/use-client-config";
import { sessionTracking } from "@/services/analytics/session-tracking";
import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router";

import {
  globalWebSocketManager,
  type GlobalWebSocketEvent,
} from "../services/global-websocket-manager";

/**
 * Hook for global shot detection
 *
 * This hook:
 * - Listens for shots from anywhere in the app
 * - Automatically navigates to session page when shot is detected on welcome page
 * - Provides connection status
 *
 * Note: Shot storage is handled by globalWebSocketManager updating React context
 * Note: Session reset is handled by WelcomeScreen when it mounts
 */
export const useGlobalShotDetection = () => {
  "use no memo";
  const navigate = useNavigate();
  const location = useLocation();
  const { clientConfig } = useClientConfig();
  const isNavigatingRef = useRef(false);

  const isRegistered = !!clientConfig?.clientId;

  // Use ref to access latest location without re-creating the listener
  const locationRef = useRef(location);
  const prevPathname = locationRef.current.pathname;
  locationRef.current = location;
  if (location.pathname !== prevPathname) {
    isNavigatingRef.current = false;
  }

  useEffect(() => {
    if (!isRegistered) {
      return;
    }

    const unsubscribe = globalWebSocketManager.addListener(
      (event: GlobalWebSocketEvent) => {
        if (event.type !== "shot-detected" || !event.shot) {
          return;
        }

        const currentPath = locationRef.current.pathname;
        if (currentPath !== "/" && currentPath !== "/welcome") {
          return;
        }

        if (!isNavigatingRef.current) {
          isNavigatingRef.current = true;
          sessionTracking.start({ startedBy: "auto" });
          navigate("/session", {
            state: {
              selectedShotId: event.shot.id,
            },
          });
        }
      }
    );

    return () => {
      unsubscribe();
    };
  }, [navigate, isRegistered]);

  return {
    isConnected: globalWebSocketManager.isConnected(),
    isReady: globalWebSocketManager.isReady(),
  };
};
